import {batch, computed, effect, signal} from "@preact/signals-react";
import {createContext} from "react";
import {MenuItem} from "@mui/material";
import {drawMenuItems} from "./SelectDraw";
import {cardMenuItems} from "./SelectCard";

export const createAppState = createContext()

export default function AppState(props) {

    // primitive signals
    // project
    const projects = signal([])
    const selectedProject = signal("")
    const projectName = signal("")
    const totalBudget = signal("")
    const requestedAmount = signal("")
    const recommendedDisbursement = signal("")
    const UUID = signal("")
    const B1ID = signal("")
    const stakeholders = signal([])
    const selectedSubmitter = signal("Borrower")
    const projectPercentComplete = signal(null)
    const fundingSources =  signal([])
    const ownerAdminNTE = signal(null)
    const isBond = signal(false)
    // draw
    const draws = signal([])
    const selectedDraw = signal("")
    const drawName = signal("")
    const drawDate = signal(null)
    const drawPackageDate = signal(null)
    const lineItems = signal([])
    const invoiceEarliestDate = signal(null)
    const invoiceLatestDate = signal(null)
    const invoiceFee = signal("")
    const invoiceNumber = signal("")
    const additionalComments = signal("")
    const discrepancies = signal([])
    const drawType = signal("")
    const hasReallocation = signal(false)
    const isMatch = signal(false)
    // inspection
    const IRDate = signal(null)
    const originalCompletionDate = signal(null)
    const revisedCompletionDate = signal(null)
    // Monday cards
    const cards = signal({})
    const selectedCard = signal("")
    const selectedDocuments = signal([])
    const revisedEDRDate = signal(null)
    const hasFFE = signal(false)
    // app-specific
    const alert = signal({isOpen: false, severity: "error", message: ""})
    const accessToken = signal("")
    const isGlobalDisabled = signal(true)

    const validatePlainNumber = (value, fieldName) => {
        const rawValue = value ?? ""

        // Treat blank as 0 without warning
        if (rawValue === "" || rawValue === null || rawValue === undefined || rawValue === "0") {
            alert.value = {
                isOpen: true,
                severity: "warning",
                message: "No NTE value was entered for Owner Administration. Please review the NTE value in the Project Settings in Rabbet."
            }
        }

        const number = Number(rawValue)

        if (!Number.isFinite(number)) {
            alert.value = {
                isOpen: true,
                severity: "warning",
                message: `${fieldName} is not formatted correctly. Please enter it as a plain number without commas or currency symbols, for example: $1,200,000.00 => 1200000.00.`
            }

            return 0
        }

        return number
    }

    // reset fields
    const resetDrawFields = () => {
        batch(() => {
            drawDate.value = null
            drawType.value = ""
            drawPackageDate.value = null
            invoiceEarliestDate.value = null
            invoiceLatestDate.value = null
            drawName.value = ""
            lineItems.value = []
            recommendedDisbursement.value = ""
            invoiceNumber.value = ""
            invoiceFee.value = ""
            IRDate.value = null
            additionalComments.value = ""
            requestedAmount.value = 0
            discrepancies.value = []
            selectedDocuments.value = []
            projectPercentComplete.value = null
            revisedEDRDate.value = null
            hasReallocation.value = false
            hasFFE.value = false
            isMatch.value = false
            isBond.value = false
        })
    }

    // round to 4 decimal places
    const roundToFourDecimals = (num) => {
        return Math.round((num + Number.EPSILON) * 10000) / 10000
    }

    // selecting a new project
    effect(() => {
        // set draws selection menu upon selection of a project
        if (selectedProject.value && accessToken.value) {

            isGlobalDisabled.value = true
            const authHeader = {
                withCredentials: true,
                credentials: 'include',
                headers: {'Authorization': `Bearer ${accessToken}`}
            }
            // set project name
            projectName.value = projects.value.find(project => project.id === selectedProject.value).name
            

            // get draws for selected project
            fetch(`https://api.quickdrawfundcontrol.com/rabbet/draws/${selectedProject.value}`, authHeader)
                .then(response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        throw (response.error())
                    }
                })
                .then(data => {
                    // set draws
                    batch(() => {
                        // find selected project data
                        const projectData = projects.value.find(project => project.id === selectedProject.value)
                        // temporarily disable UI
                        isGlobalDisabled.value = true
                        // reset selected draw
                        selectedDraw.value = ""
                        // reset current budget
                        totalBudget.value = projectData.budgetAmount
                        const localUUID = projectData.customFields.customId
                        // set UUID (for BHCIP)
                        UUID.value = localUUID || ""
                        // set B1 ID (for BHCIP)
                        B1ID.value = projectData.customFields.b1Id || ""
                        // is this a BOND project?
                        isBond.value = B1ID.value.includes("BOND")
                        // set requester type
                        localUUID ? selectedSubmitter.value = "Sponsor" : selectedSubmitter.value = "Borrower"
                        // set original completion date
                        originalCompletionDate.value = projectData.customFields.origdateofcomp
                        // revised date of completion
                        revisedCompletionDate.value = projectData.customFields.revdateofcomp
                        // owner admin NTE value
                        ownerAdminNTE.value = validatePlainNumber(
                            projectData.customFields.ownerAdminNte,
                            "Owner Admin NTE"
                        )
                        // reset draw-level fields
                        resetDrawFields()
                        if (data.data.length <= 0) {
                            draws.value = []
                            throw new Error("No draws have been entered for this project.")
                        }
                        // set draw menu items
                        draws.value = data.data
                        drawMenuItems.value = draws.value.map((draw) => {
                            return (
                                <MenuItem
                                    key={draw.id}
                                    value={draw.id}
                                >
                                    {draw.name}
                                </MenuItem>
                            )
                        })
                        // set card menu items
                        cardMenuItems.value = cards.value.map((card) => {
                            return (
                                <MenuItem
                                    key={card.id}
                                    value={card.id}
                                >
                                    {card.name}
                                </MenuItem>
                            )
                        })
                    })
                })
                .catch((error) => {
                    alert.value = {isOpen: true, severity: "error", message: error.message}
                })
                .finally(() => {
                    isGlobalDisabled.value = false
                })

            // get stakeholders for selected project
            fetch(`https://api.quickdrawfundcontrol.com/rabbet/stakeholders/${selectedProject.value}`, authHeader)
                .then(response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        throw (response.error())
                    }
                })
                .then(data => {
                    // set stakeholders
                    stakeholders.value = data.data
                })
                .catch((error) => {
                    alert.value = {isOpen: true, severity: "error", message: error.message}
                    return []
                })

            // get funding sources for the selected project
            fetch(`https://api.quickdrawfundcontrol.com/rabbet/funding_sources/${selectedProject.value}`, authHeader)
                .then(response => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        throw (response.error())
                    }
                })
                .then(data => {
                    // set stakeholders
                    fundingSources.value = data.data
                })
                .catch((error) => {
                    alert.value = {isOpen: true, severity: "error", message: error.message}
                    return []
                })
        }
    })

    // select new draw
    effect(() => {
        if (selectedDraw.value && draws.value.length > 0) {
            batch(() => {
                resetDrawFields()
                // selectedCard.value = ""
                requestedAmount.value = draws.value.find(draw => draw.id === selectedDraw.value).requestedAmount
                recommendedDisbursement.value = requestedAmount.value
                mapLineItems() // Map the lineItems
                mapDiscrepancies()
                drawName.value = draws.value.find(draw => draw.id === selectedDraw.value).name
                isGlobalDisabled.value = false
            })
        }
        return 0
    })

    // calculate deductions
    const totalDeductions = computed(() => {
        if (requestedAmount.value > 0 && recommendedDisbursement.value > 0) {
            return requestedAmount.value - recommendedDisbursement.value
        }
        return 0
    })

    // calculate percent remaining value
    const calculatePercentRemaining = (currentBudget, requestedToDateAmount) => {
        const budget = Number(currentBudget)
        const requested = Number(requestedToDateAmount)

        if (!Number.isFinite(budget) || budget <= 0) return 0
        if (!Number.isFinite(requested) || requested < 0) return 0

        const percentRemaining = (budget - requested) / budget
        const clamped = Math.max(0, Math.min(percentRemaining, 1))

        return roundToFourDecimals(clamped)
    }

    // calculate percent complete value
    const calculatePercentComplete = (currentBudget, requestedToDateAmount) => {
        const budget = Number(currentBudget)
        const requested = Number(requestedToDateAmount)

        if (!Number.isFinite(budget) || budget <= 0) return 0
        if (!Number.isFinite(requested) || requested < 0) return 0

        return roundToFourDecimals(requested / budget)
    }

    // round decimal places
    const roundDecimal = (num) => {
        return (Math.ceil(num * 100) / 100).toFixed(2)
    }

    // map line items
    // it is important to keep the raw data here, and only transform the style in the UI value field
    const mapLineItems = () => {
        try {
            // Map line items from the selected draw
            const sovLineItems = draws.value.find(draw => draw.id === selectedDraw.value).lineItems.map(item => {
                return {
                    id: item.id,
                    name: item.name,
                    division: item.division.name,
                    originalBudgetAmount: item.originalBudgetAmount,
                    budgetAmount: item.budgetAmount,
                    adjustmentsAmount: item.adjustmentsAmount,
                    previousAdjustmentsAmount: item.previousAdjustmentsAmount,
                    requestedAmount: item.requestedAmount,
                    requestedToDateAmount: item.requestedToDateAmount - item.requestedAmount,
                    balanceToFundAmount: item.budgetAmount - item.requestedToDateAmount,
                    percentRemaining: roundDecimal(calculatePercentRemaining(item.budgetAmount, item.requestedToDateAmount)),
                    percentComplete: roundDecimal(calculatePercentComplete(item.budgetAmount, item.requestedToDateAmount)),
                    inspectionPercentComplete: item.inspectionPercentComplete || 0,
                    hardCosts: item.hardCosts,
                    inspectionNotes: item.inspectionNotes,
                };
            });

            // Build Owner Admin subtotal line item, based on items named "Owner Administration"
            const rawItems = draws.value.find(draw => draw.id === selectedDraw.value).lineItems || [];
            const ownerAdminNamedItems = rawItems.filter(it => {
                const nm = (it.name || "").trim().toLowerCase();
                return nm === "owner administration";
            });

            let ownerAdminLineItem = null;
            if (ownerAdminNamedItems.length > 0) {
                const totals = ownerAdminNamedItems.reduce((acc, it) => {
                    acc.originalBudgetAmount += (it.originalBudgetAmount || 0);
                    acc.budgetAmount += (it.budgetAmount || 0);
                    acc.adjustmentsAmount += (it.adjustmentsAmount || 0);
                    acc.previousAdjustmentsAmount += (it.previousAdjustmentsAmount || 0);
                    acc.requestedAmount += (it.requestedAmount || 0);
                    acc.requestedToDateAmount += (it.requestedToDateAmount || 0);
                    return acc;
                }, { originalBudgetAmount: 0, budgetAmount: 0, adjustmentsAmount: 0, previousAdjustmentsAmount: 0, requestedAmount: 0, requestedToDateAmount: 0 });

                const percentComplete = calculatePercentComplete(ownerAdminNTE.value, totals.requestedToDateAmount);
                const percentRemaining = calculatePercentRemaining(ownerAdminNTE.value, totals.requestedToDateAmount);

                // Warn if Owner Admin percentComplete is greater than 1 (100%)
                if (percentComplete > 1) {
                    alert.value = {
                        isOpen: true,
                        severity: "warning",
                        message: `Owner Admin percent-complete exceeds 100% (${(percentComplete * 100).toFixed(2)}%). Please review NTE and requested-to-date amounts.`
                    }
                }

                ownerAdminLineItem = {
                    id: `owner-admin-subtotal-${selectedDraw.value}`,
                    name: "Owner Admin Subtotal",
                    division: "Owner Admin",
                    originalBudgetAmount: ownerAdminNTE.value,
                    budgetAmount: ownerAdminNTE.value,
                    adjustmentsAmount: 0,
                    previousAdjustmentsAmount: 0,
                    requestedAmount: totals.requestedAmount,
                    requestedToDateAmount: totals.requestedToDateAmount,
                    balanceToFundAmount: ownerAdminNTE - totals.requestedToDateAmount,
                    percentRemaining: percentRemaining,
                    percentComplete: percentComplete,
                    inspectionPercentComplete: 0,
                    hardCosts: false,
                    inspectionNotes: null,
                };
            }

            // Extract grant funding source and create a new line item if it exists
            const grantFundingSource = fundingSources.value.find(
                source =>
                    source.equityType === "loan" &&
                    ["state", "grant"].some(keyword =>
                        source.label?.toLowerCase().includes(keyword)
                    )
            );

            if (grantFundingSource) {
                // Convert amount from cents to dollars by dividing by 100
                const grantAmount = grantFundingSource.amount / 100
                const fundedToDate = grantFundingSource.fundedToDate / 100
                const grantPercentRemaining = calculatePercentRemaining(grantAmount, fundedToDate)
                const grantPercentComplete = calculatePercentComplete(grantAmount, fundedToDate)

                // Create the grant line item
                const grantLineItem = {
                    id: grantFundingSource.id,
                    name: "Grant Total",
                    division: "Grant Funds",
                    originalBudgetAmount: grantAmount,
                    budgetAmount: grantAmount,
                    adjustmentsAmount: 0,
                    previousAdjustmentsAmount: 0,
                    requestedAmount: 0,
                    requestedToDateAmount: fundedToDate,
                    balanceToFundAmount: grantAmount,
                    percentRemaining: grantPercentRemaining,
                    percentComplete: grantPercentComplete,
                    inspectionPercentComplete: 0,
                    hardCosts: false,
                    inspectionNotes: null,
                };

                // Warn if grant line item exists but has a budget of $0
                if (grantLineItem && grantLineItem.budgetAmount === 0) {
                    alert.value = {
                        isOpen: true,
                        severity: "warning",
                        message: `The grant budget is $0. Please review and update the Funding Sources in Rabbet.`
                    }
                }

                // Assign the line items array, including the new subtotal(s)
                lineItems.value = ownerAdminLineItem ? [...sovLineItems, ownerAdminLineItem, grantLineItem] : [...sovLineItems, grantLineItem];

            } else {
                // If no grant funding source exists, simply assign sovLineItems plus Owner Admin subtotal if available
                lineItems.value = ownerAdminLineItem ? [...sovLineItems, ownerAdminLineItem] : sovLineItems;
            }

            // console.log(lineItems.value)
        } catch (error) {
            // Surface any unexpected mapping errors to the user
            alert.value = { isOpen: true, severity: "error", message: error?.message || "Failed to map line items." };
        }
    };

    /*check the line items for a one that contains the word "match" and if it exists
    * and has a requested amount greater than zero, return true, otherwise return false.*/
    function checkMatchAmount(lineItems) {
        return lineItems.value.some(
            item =>
                (item.name.toLowerCase().includes("match") ||
                    item.name.toLowerCase().includes("verified cash")) &&
                item.requestedAmount > 0
        )
    }

    /*check the line items for one where there is a reallocation amount, otherwise return false.*/
    function checkReallocation(lineItems) {
        return lineItems.value.find(item => item.adjustmentsAmount > 0)
    }

    // map discrepancy comments for the covers sheet
    const mapDiscrepancies = () => {
        discrepancies.value = draws.value.find(draw => draw.id === selectedDraw.value).lineItems
            .filter(item => item.inspectionNotes !== null)
            .map(item => item.inspectionNotes)
    }

    // selecting a new Monday card
    effect(() => {
        if (selectedCard.value) {
            const cardData = cards.value.find(card => card.id === selectedCard.value)
            batch(() => {
                drawDate.value = cardData.drawDate
                drawPackageDate.value = cardData.drawPackageDate
                revisedEDRDate.value = cardData.revisedEDRDate
                invoiceEarliestDate.value = cardData.invoiceEarliestDate
                invoiceLatestDate.value = cardData.invoiceLatestDate
                IRDate.value = cardData.IRDate
                projectPercentComplete.value = cardData.projectPercentComplete ? cardData.projectPercentComplete : 0
                additionalComments.value = cardData.additionalComments
                drawType.value = cardData.drawType
                hasFFE.value = cardData.hasFFE
                isMatch.value = checkMatchAmount(lineItems)
                hasReallocation.value = checkReallocation(lineItems)
            })
        }
    })

    return (
        <createAppState.Provider value={{
            projects,
            selectedProject,
            projectName,
            fundingSources,
            cards,
            selectedCard,
            selectedDraw,
            drawName,
            drawType,
            lineItems,
            invoiceEarliestDate,
            invoiceLatestDate,
            drawDate,
            drawPackageDate,
            revisedEDRDate,
            selectedSubmitter,
            recommendedDisbursement,
            selectedDocuments,
            IRDate,
            projectPercentComplete,
            originalCompletionDate,
            revisedCompletionDate,
            invoiceFee,
            invoiceNumber,
            additionalComments,
            discrepancies,
            alert,
            isGlobalDisabled,
            accessToken,
            totalDeductions,
            stakeholders,
            totalBudget,
            requestedAmount,
            draws,
            UUID,
            B1ID,
            isMatch,
            hasFFE,
            hasReallocation,
        }}>
            {props.children}
        </createAppState.Provider>
    )
}